import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/motion";
import { ProductCard } from "@/pages/Home";
import { CATEGORIES } from "@/data/products";
import { useSite } from "@/content";

const Collections = () => {
  const { products } = useSite();
  const [active, setActive] = useState("All");
  const filtered = useMemo(
    () => (active === "All" ? products : products.filter((p) => p.category === active)),
    [products, active]
  );

  return (
    <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-32 md:px-10 md:pt-44" data-testid="collections-page">
      <FadeUp>
        <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">
          The full SanCub range — tapware to towel bars
        </p>
      </FadeUp>
      <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <h1 className="font-display text-6xl font-medium leading-[1.02] tracking-tight text-ink md:text-8xl" data-testid="collections-headline">
          <MaskedLine>Collections</MaskedLine>
        </h1>
        <FadeUp delay={0.3}>
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="product-count">
            {filtered.length} {filtered.length === 1 ? "product" : "products"}
          </p>
        </FadeUp>
      </div>

      <FadeUp delay={0.35} className="mt-12 -mx-6 px-6 md:mx-0 md:px-0" data-testid="filter-bar">
        <div className="no-scrollbar flex flex-nowrap gap-2.5 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible md:pb-0">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setActive(c)}
            className={`shrink-0 rounded-full px-5 py-2.5 text-[13px] font-semibold transition duration-300 ${
              active === c
                ? "bg-ink text-white"
                : "bg-white text-ink/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:text-ink"
            }`}
          >
            {c}
          </button>
        ))}
        </div>
      </FadeUp>

      <motion.div layout className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
        <AnimatePresence mode="popLayout">
          {filtered.map((p) => (
            <motion.div
              key={p.id}
              layout
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <ProductCard p={p} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <FadeUp className="mt-24">
        <div className="flex flex-col items-start gap-8 rounded-[3rem] bg-white p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:flex-row md:items-center md:justify-between md:p-14">
          <p className="max-w-lg font-display text-3xl font-medium leading-[1.15] tracking-tight text-ink md:text-4xl">
            Trade, wholesale or a full fit-out — send us your schedule.
          </p>
          <a
            href="/contact"
            data-testid="collections-enquire-link"
            className="group flex shrink-0 items-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.03] hover:bg-ink/90"
          >
            Enquire now
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </FadeUp>
    </main>
  );
};

export default Collections;
